<script>
  /**
   * The reseller's own view of itself.
   *
   * Deliberately small: what a partner account needs to know about its own
   * organisation is who it is, whether its certification stands, and what it is
   * therefore allowed to do — plus the two doors into the work itself. The
   * customer list and the compliance reports are the same pages internal users
   * see, scoped on the server.
   */
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import Layout from '../components/Layout.svelte';
  import { partnersApi, serviceEnrollmentsApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import { Handshake, Loader, AlertCircle, ShieldCheck, ShieldAlert, Users, FileCheck, CalendarClock } from 'lucide-svelte';

  let loading = true;
  let error = null;
  let partner = null;
  let membership = null;
  let renewals = [];

  onMount(async () => {
    try {
      const response = await partnersApi.getMine();
      partner = response.partner;
      membership = response.membership;
    } catch (err) {
      console.error('[MyPartner] load failed:', err);
      error = err.message;
    } finally {
      loading = false;
    }

    try {
      // Scoped and projected server-side: a reseller sees its own customers'
      // subscriptions without our cost of delivering them. A failure here must
      // not take the page down with it.
      const response = await serviceEnrollmentsApi.getRenewals();
      renewals = response.renewals || [];
    } catch (err) {
      console.error('[MyPartner] renewals failed:', err);
    }
  });

  function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('it-IT', { dateStyle: 'medium' });
  }
</script>

<Layout>
  <div class="space-y-6">
    <div class="flex items-center space-x-4">
      <div class="p-3 rounded-2xl shadow-lg" style="background: linear-gradient(to bottom right, #00bfff, #0090cc);">
        <Handshake size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('partners.myTitle')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('partners.mySubtitle')}</p>
      </div>
    </div>

    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if error}
      <div class="card p-8 text-center">
        <AlertCircle size={40} class="mx-auto text-red-500 mb-3" />
        <p class="text-gray-600 dark:text-gray-300">{$_('partners.notPartner')}</p>
        <p class="text-xs text-gray-400 mt-2">{error}</p>
      </div>
    {:else if partner}
      <div class="card p-6">
        <div class="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white">{partner.branding?.displayName || partner.name}</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 font-mono">{partner.id}</p>
            {#if partner.vatNumber}
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{partner.vatNumber}</p>
            {/if}
          </div>
          {#if partner.branding?.logoUrl}
            <img src={partner.branding.logoUrl} alt={partner.name} class="h-12 object-contain" />
          {/if}
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div class="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <p class="text-xs uppercase tracking-wider text-gray-400 mb-1">{$_('partners.status')}</p>
            <p class="font-semibold text-gray-900 dark:text-white">{$_(`partners.statuses.${partner.status}`)}</p>
          </div>
          <div class="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <p class="text-xs uppercase tracking-wider text-gray-400 mb-1">{$_('partners.certified')}</p>
            <p class="font-semibold flex items-center space-x-2 {partner.certified ? 'text-emerald-600' : 'text-amber-600'}">
              {#if partner.certified}<ShieldCheck size={18} />{:else}<ShieldAlert size={18} />{/if}
              <span>{partner.certified ? $_('partners.certifiedYes') : $_('partners.certifiedNo')}</span>
            </p>
            <p class="text-xs text-gray-400 mt-1">{$_('partners.certifiedUntil')}: {formatDate(partner.certifiedUntil)}</p>
          </div>
          <div class="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
            <p class="text-xs uppercase tracking-wider text-gray-400 mb-1">{$_('partners.myRole')}</p>
            <p class="font-semibold text-gray-900 dark:text-white">
              {membership?.partnerRole === 'admin' ? $_('partners.roleAdmin') : $_('partners.roleSales')}
            </p>
            <p class="text-xs mt-1 {membership?.canSell ? 'text-emerald-600' : 'text-gray-400'}">
              {membership?.canSell ? $_('partners.canSellYes') : $_('partners.canSellNo')}
            </p>
            <p class="text-xs mt-0.5 {membership?.canAdmin ? 'text-emerald-600' : 'text-gray-400'}">
              {membership?.canAdmin ? $_('partners.canAdminYes') : $_('partners.canAdminNo')}
            </p>
          </div>
        </div>
        <p class="text-xs text-gray-400 mt-3">{$_('partners.capabilityHint')}</p>
      </div>

      <!-- What renews, and when: the reseller's own working list -->
      <div class="card p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center space-x-2">
          <CalendarClock size={20} class="text-primary-500" />
          <span>{$_('partners.renewalsTitle')}</span>
        </h3>
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">{$_('partners.renewalsHint')}</p>

        {#if renewals.length === 0}
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('partners.noRenewals')}</p>
        {:else}
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.renewalCustomer')}</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.renewalService')}</th>
                  <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.renewalQuantity')}</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.renewalTermEnd')}</th>
                  <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('partners.renewalDays')}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each renewals as renewal (renewal.id)}
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td class="px-3 py-3 text-sm text-gray-900 dark:text-white">{renewal.customerName}</td>
                    <td class="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">{renewal.variantName || renewal.variantCode}</td>
                    <td class="px-3 py-3 text-sm text-right text-gray-600 dark:text-gray-300">{renewal.quantity}</td>
                    <td class="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDate(renewal.termEnd)}</td>
                    <td class="px-3 py-3 text-sm text-right font-medium {renewal.renewalCheckpointDays <= 15 ? 'text-amber-600' : 'text-gray-600 dark:text-gray-300'}">
                      {renewal.renewalCheckpointDays ?? '—'}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <a href="/customers" use:link class="card p-6 hover:border-primary-400 transition-colors flex items-center space-x-4">
          <Users size={28} class="text-primary-500" />
          <div>
            <p class="font-semibold text-gray-900 dark:text-white">{$_('partners.goToCustomers')}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{partner.customerCount ?? 0}</p>
          </div>
        </a>
        <a href="/compliance-reports" use:link class="card p-6 hover:border-primary-400 transition-colors flex items-center space-x-4">
          <FileCheck size={28} class="text-secondary-500" />
          <div>
            <p class="font-semibold text-gray-900 dark:text-white">{$_('partners.goToReports')}</p>
          </div>
        </a>
      </div>
    {/if}
  </div>
</Layout>
